import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AssessmentRunOption } from '../../shared/utils';
import IpptInnerForm from '../IpptInnerForm';
import type { CreateIpptPerformanceFormData } from '../IpptPerformanceFormConfig';
import { createIpptPerformanceFormSchema, ipptPerformanceDefaultFormValues } from '../IpptPerformanceFormConfig';
import { SubmitPerformanceIpptResultsMutation } from './CreatePerformanceIpptResults.graphql';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useActiveRole, useUser } from '@/contexts/UserSession';
import { UserRole } from '@/gql/enums';
import type { PerformanceResultUserFragment, SubmitIpptResultInput } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import '@/utils/notifications';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';

interface CreatePerformanceIpptModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    groupUsers: PerformanceResultUserFragment[];
    assessmentRunOptions: AssessmentRunOption[];
}

const CreatePerformanceIpptModal = ({
    open,
    onClose,
    groupUsers,
    assessmentRunOptions,
}: CreatePerformanceIpptModalProps) => {
    const { t } = useTranslation('performanceResult');
    const { getRoleLabel } = useCompanyRoleLabels();
    const user = useUser(true);
    const activeRole = useActiveRole();

    const isTrainee = useMemo(() => {
        return activeRole && activeRole.role === UserRole.Trainee;
    }, [activeRole]);

    const form = useForm<CreateIpptPerformanceFormData>({
        mode: 'onChange',
        resolver: zodResolver(createIpptPerformanceFormSchema),
        defaultValues: { ...ipptPerformanceDefaultFormValues, userId: isTrainee ? user.id : undefined },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (!isTiedToAssessmentRun) {
            form.setValue('assessmentRunId', '');
        }
    }, [form, isTiedToAssessmentRun]);

    const handleSubmit = useCallback(async () => {
        if (!(await form.trigger())) {
            return;
        }

        const values = form.getValues();

        const request: SubmitIpptResultInput = {
            assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId : undefined,
            userId: values.userId,
            completedAt: values.date,
            result: {
                pushUps: values.pushUps ?? undefined,
                sitUps: values.sitUps ?? undefined,
                pullUps: values.pullUps ?? undefined,
                runTimeSec: values.runTime ?? undefined,
                points: values.points ?? undefined,
            },
        };

        try {
            const { data } = await client.mutate({
                mutation: SubmitPerformanceIpptResultsMutation,
                variables: {
                    input: request,
                },
            });

            if (data?.submitIpptResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.submitIpptResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('createError.description', { type: 'IPPT' });

                showErrorNotification({
                    message: t('createError.title', { type: 'IPPT' }),
                    description: errorMessage,
                });
                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('createSuccess.title', { type: 'IPPT' }),
                    description: t('createSuccess.message', { type: 'IPPT' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error submitting IPPT performance result:', error);
            throw error;
        }
    }, [form, onClose, t]);

    const userOptions = useMemo(() => {
        const options = [];
        for (const user of groupUsers) {
            if (user.__typename === 'TenantUser') {
                options.push({ label: user.name, value: user.id });
            }
        }
        return options;
    }, [groupUsers]);

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button variant="secondary" onClick={() => onClose()}>
                    {t('ippt.createModal.cancelBtn')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t('ippt.createModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('ippt.createModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <SelectField
                                name="userId"
                                label={t('ippt.form.traineeName', { role: getRoleLabel(UserRole.Trainee) })}
                                placeholder={t('ippt.form.traineeNamePlaceholder', {
                                    role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                                })}
                                searchable
                                options={userOptions}
                                required
                                disabled={!!isTrainee}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('ippt.form.assessmentDate')} required />
                        </div>
                        <IpptInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default CreatePerformanceIpptModal;
